import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MenuService } from '../service/menu.service';
import { UserService } from '../service/user.service';
import { NavigationConfigService, NavigationTab_Dto } from '../service/navigation-config.service';

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.css'],
})
export class TabsComponent implements OnInit, OnDestroy {
  role: any;
  selectedTab?: string;
  selectedSidebar!: string;
  clraType: any;
  showTabs: boolean = true;
  currentRoute: string = this.router.url;
  mockTabs: any = [];
  selectedModuleName: string = '';

  // Closes every long-lived subscription opened in ngOnInit. This component sits inside
  // an *ngIf in the layout, so it is destroyed and recreated as the user moves around.
  // Without a teardown each instance's observers stayed registered on the root-scoped
  // services for the life of the browser tab.
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly menuService: MenuService,
    private readonly user: UserService,
    private readonly navConfigService: NavigationConfigService
  ) { }

  navigateToMock(component: string) {
    this.selectedTab = component;
    const routesWithState: string[] = ['AuditSubContractor', 'AuditsContractor'];
    if (routesWithState.includes(component)) {
      const navigationState = history.state;
      this.router.navigate([`/${component}`], {
        state: navigationState
      });
    } else {
      this.router.navigate([`/${component}`]);
    }
  }

  isTabSelectedMock(component: string): boolean {
    this.currentRoute = component;
    return this.selectedTab === component;
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.role = this.user.getRole();

    this.menuService.selectedMenu$
      .pipe(takeUntil(this.destroy$))
      .subscribe((menu) => {
        this.selectedSidebar = menu || '';

        // Tabs come from the server-side, role-scoped navigation config. The old
        // src/data/tabData.ts exposed every role's tab bars in the JS bundle, and the
        // customer-specific 'Unlocked Days' rule was a hardcoded customer-id array in
        // this file. Both now live in dbo.RoleModuleTabMapping: the API filters by role
        // AND customer from the JWT claims, so only the module is filtered here.
        //
        // Prefer the cache: authRouteGuard fetches the config and AWAITS it before it
        // activates a protected route, so by the time this component exists the config
        // is already present and the tabs render synchronously — no request and no
        // failure mode. Only fall back to a fetch if the cache is somehow cold, and only
        // when there is a token to send (GetNavigationConfig is [Authorize]).
        const cached = this.navConfigService.getConfigSync();
        if (cached) {
          this.applyTabs(cached.tabs);
          return;
        }
        if (!this.user.getAccessToken()) {
          // No session yet — leave the current tabs alone rather than blanking the bar.
          // selectedMenu$ emits again on the next sidebar click, by which point the
          // session exists and the cache is warm.
          return;
        }
        this.navConfigService.getConfig()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (config) => this.applyTabs(config.tabs),
            error: () => { this.mockTabs = []; }
          });
      });

    // Subscribed ONCE, at the top level. This handler never used the emitted menu, but
    // nesting it inside the selectedMenu$ callback added a further permanent
    // NavigationEnd subscription on every sidebar click — so after N clicks the same
    // work ran N times per navigation. selectedMenu$ is a BehaviorSubject that emits
    // synchronously on subscribe, so the handler is still registered during this same
    // ngOnInit and the visible behaviour is unchanged.
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.selectedTab = event.urlAfterRedirects.slice(1);
          this.showTabs = this.mockTabs?.some(
            (tab: any) => tab.component === this.selectedTab
          ) ?? false;
        }
      });
  }

  /** Build the tab bar for the currently selected module and pick the active tab. */
  private applyTabs(tabs: NavigationTab_Dto[]): void {
    this.mockTabs = (tabs ?? [])
      .filter((t) => t.moduleName === this.selectedSidebar)
      .map((t) => ({ tab: t.tabName, component: t.componentRoute, sort: t.sortOrder }));

    this.selectedTab =
      this.mockTabs.find(
        (t: any) =>
          t.component.toLowerCase() ===
          this.router.url.split('/').pop()?.toLowerCase()
      )?.component || this.mockTabs[0]?.component;
  }

  applySelectedTabClass(): void {
    setTimeout(() => {
      const elements = document.querySelectorAll('.listItem');
      elements.forEach((element: Element) => {
        if (element.innerHTML.includes(this.selectedTab ?? '')) {
          this.renderer.addClass(element, 'selected-tab');
        } else {
          this.renderer.removeClass(element, 'selected-tab');
        }
      });
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
